import { useState } from "react";
import { Link } from "react-router-dom";
import {
  LogIn, User, Lock, ArrowRight, ArrowLeft, QrCode, Calendar, Wrench, Download,
  ChefHat, Coffee, Snowflake, Zap, Search, CheckCircle2, Clock, AlertCircle, MessageSquare
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useLang } from "../context/LanguageContext";

// Demo data — in production this would come from an authenticated backend
const DEMO_EMAIL = "admin@mariotkitchen.com";
const DEMO_PASSWORD = "demo123";

const DEMO_EQUIPMENT = [
  { id: "MK-2401", name_en: "Rational iCombi Pro XS", name_ar: "راشونال iCombi Pro XS", type: "oven", brand: "Rational", installed: "2024-03-15", warranty: "2027-03-15", status: "operational", lastService: "2026-01-08" },
  { id: "MK-2402", name_en: "Coldline W40 Blast Chiller", name_ar: "كولد لاين W40 مبرد صدمة", type: "refrigeration", brand: "Coldline", installed: "2024-04-02", warranty: "2027-04-02", status: "operational", lastService: "2026-01-12" },
  { id: "MK-2403", name_en: "Fimar 4-Burner Range", name_ar: "فيمار موقد 4 شعلات", type: "cooking", brand: "Fimar", installed: "2024-04-02", warranty: "2026-04-02", status: "service_due", lastService: "2025-11-20" },
  { id: "MK-2404", name_en: "Hobart Pass-Through Dishwasher", name_ar: "هوبارت غسالة صحون", type: "warewashing", brand: "Hobart", installed: "2024-04-10", warranty: "2027-04-10", status: "in_service", lastService: "2026-02-01" },
  { id: "MK-2405", name_en: "La Marzocco Linea PB (3-Group)", name_ar: "لا مارزوكو Linea PB", type: "beverage", brand: "La Marzocco", installed: "2024-05-01", warranty: "2027-05-01", status: "operational", lastService: "2026-01-15" },
  { id: "MK-2406", name_en: "Inofrigo Walk-in Cold Room", name_ar: "إينوفريغو غرفة تبريد", type: "refrigeration", brand: "Inofrigo", installed: "2024-03-15", warranty: "2029-03-15", status: "operational", lastService: "2025-12-20" },
];

const SERVICE_HISTORY = [
  { date: "2026-02-01", equipment: "MK-2404 · Hobart Dishwasher", type: "Repair", tech: "Ahmed K.", note: "Replaced door gasket. Full function restored.", status: "completed" },
  { date: "2026-01-15", equipment: "MK-2405 · La Marzocco Espresso", type: "Preventive", tech: "Karim S.", note: "Backflush, grouphead descale, gasket check.", status: "completed" },
  { date: "2026-01-12", equipment: "MK-2402 · Coldline Blast Chiller", type: "Preventive", tech: "Ahmed K.", note: "Condenser clean, refrigerant pressure check.", status: "completed" },
  { date: "2026-01-08", equipment: "MK-2401 · Rational iCombi", type: "Preventive", tech: "Omar F.", note: "Deep clean cycle, sensor calibration.", status: "completed" },
  { date: "2026-02-18", equipment: "MK-2403 · Fimar Range", type: "Preventive", tech: "Scheduled — Karim S.", note: "Quarterly gas & burner service.", status: "scheduled" },
];

const TYPE_ICONS = { oven: ChefHat, refrigeration: Snowflake, cooking: Zap, warewashing: Wrench, beverage: Coffee };

export default function ClientPortal() {
  const { t, lang, isRTL } = useLang();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const p = t.clientPortal;

  // Persist "auth" locally for demo
  const [loggedIn, setLoggedIn] = useState(() => localStorage.getItem("mariot_client_auth") === "1");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("assets");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const login = (e) => {
    e.preventDefault();
    if (email.trim().toLowerCase() === DEMO_EMAIL && password === DEMO_PASSWORD) {
      localStorage.setItem("mariot_client_auth", "1");
      setLoggedIn(true);
      setError("");
    } else {
      setError(p.loginError);
    }
  };

  const logout = () => {
    localStorage.removeItem("mariot_client_auth");
    setLoggedIn(false);
    setEmail(""); setPassword("");
  };

  const useDemo = () => { setEmail(DEMO_EMAIL); setPassword(DEMO_PASSWORD); };

  // LOGIN VIEW
  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-red-50/30 flex items-center justify-center py-16 px-4" data-testid="client-portal-login">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#0891b2] mb-4">
              <User className="w-6 h-6 text-white" />
            </div>
            <div className="text-[10px] tracking-[0.35em] text-[#ef4444] font-semibold uppercase mb-2">{p.eyebrow}</div>
            <h1 className="font-display text-3xl text-[#0891b2] mb-2">{p.loginTitle}</h1>
            <p className="text-slate-600 text-sm">{p.loginSubtitle}</p>
          </div>

          <form onSubmit={login} className="bg-white rounded-2xl border border-slate-200 shadow-lg p-6 sm:p-8 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">{p.email}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="portal-email" className="h-11" placeholder="admin@mariotkitchen.com" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">{p.password}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="portal-password" className="h-11" />
            </div>
            {error && <div data-testid="portal-error" className="text-xs text-[#ef4444] bg-red-50 border border-red-100 rounded-lg p-2.5">{error}</div>}
            <Button type="submit" data-testid="portal-login-btn" className="w-full h-11 bg-[#0891b2] hover:bg-[#0e7490] text-white font-semibold">
              <LogIn className="w-4 h-4 me-2" /> {p.loginBtn}
            </Button>
            <button type="button" onClick={useDemo} data-testid="portal-demo" className="w-full text-xs text-[#ef4444] hover:text-[#dc2626] font-semibold py-2">
              {p.useDemoCredentials}
            </button>
            <div className="text-center text-xs text-slate-500 pt-3 border-t border-slate-100">
              {p.notClient} <Link to="/maintenance" className="text-[#ef4444] font-semibold hover:underline">{p.explorePlans}</Link>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // DASHBOARD VIEW
  const filtered = DEMO_EQUIPMENT.filter((e) =>
    (lang === "ar" ? e.name_ar : e.name_en).toLowerCase().includes(query.toLowerCase()) ||
    e.id.toLowerCase().includes(query.toLowerCase()) ||
    e.brand.toLowerCase().includes(query.toLowerCase())
  );

  const statusColor = (s) => ({
    operational: "bg-green-100 text-green-700",
    service_due: "bg-amber-100 text-amber-700",
    in_service: "bg-cyan-100 text-cyan-700",
  })[s] || "bg-slate-100 text-slate-600";

  const kpis = [
    { label: p.kpi1, value: DEMO_EQUIPMENT.length, color: "text-[#0891b2]" },
    { label: p.kpi2, value: DEMO_EQUIPMENT.filter(e => e.status === "operational").length, color: "text-green-600" },
    { label: p.kpi3, value: DEMO_EQUIPMENT.filter(e => e.status === "service_due").length, color: "text-amber-600" },
    { label: p.kpi4, value: SERVICE_HISTORY.filter(s => s.status === "scheduled").length, color: "text-[#ef4444]" },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-portal-dashboard">
      {/* Portal header */}
      <section className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{p.eyebrow}</div>
            <div className="font-display text-lg text-[#0891b2]">Blue Fig Restaurant Group</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs text-slate-500">{p.plan} · <span className="text-[#ef4444] font-semibold">Professional</span></span>
            <button type="button" onClick={logout} data-testid="portal-logout" className="text-xs text-slate-600 hover:text-[#ef4444] font-medium px-3 py-1.5 border border-slate-200 rounded-lg hover:border-[#ef4444]">
              {p.logout}
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-8">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {kpis.map((k, i) => (
            <div key={i} data-testid={`kpi-${i}`} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className={`font-display text-3xl ${k.color} mb-1`}>{k.value}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500">{k.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-slate-200">
          {[
            { key: "assets", label: p.tabAssets },
            { key: "history", label: p.tabHistory },
            { key: "schedule", label: p.tabSchedule },
          ].map((tt) => (
            <button
              key={tt.key}
              type="button"
              data-testid={`tab-${tt.key}`}
              onClick={() => setTab(tt.key)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                tab === tt.key ? "text-[#0891b2] border-[#0891b2]" : "text-slate-500 border-transparent hover:text-[#0891b2]"
              }`}
            >
              {tt.label}
            </button>
          ))}
        </div>

        {/* Assets tab */}
        {tab === "assets" && (
          <div>
            <div className="relative mb-4 max-w-md">
              <Search className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? "right-3" : "left-3"} w-4 h-4 text-slate-400`} />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="asset-search"
                placeholder={p.searchAssets}
                className={`h-11 ${isRTL ? "pr-10" : "pl-10"}`}
              />
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((e) => {
                const Icon = TYPE_ICONS[e.type] || Wrench;
                return (
                  <div
                    key={e.id}
                    data-testid={`asset-${e.id}`}
                    className="bg-white rounded-xl border border-slate-200 hover:border-[#0891b2] hover:shadow-md transition-all p-5"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#0891b2]" />
                      </div>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${statusColor(e.status)} font-semibold`}>
                        {p.statuses[e.status]}
                      </span>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">{e.id}</div>
                    <h3 className="font-display text-base text-[#0891b2] mb-2 leading-snug line-clamp-2">{lang === "ar" ? e.name_ar : e.name_en}</h3>
                    <div className="text-xs text-slate-600 space-y-1">
                      <div className="flex justify-between"><span>{p.brand}</span><span className="font-semibold text-slate-700">{e.brand}</span></div>
                      <div className="flex justify-between"><span>{p.warranty}</span><span className="font-semibold text-slate-700" dir="ltr">{e.warranty}</span></div>
                      <div className="flex justify-between"><span>{p.lastService}</span><span className="font-semibold text-slate-700" dir="ltr">{e.lastService}</span></div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                      <button
                        type="button"
                        onClick={() => setSelected(e)}
                        data-testid={`qr-${e.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-[#0891b2] hover:bg-cyan-50 py-2 rounded transition-colors"
                      >
                        <QrCode className="w-3.5 h-3.5" /> {p.viewQR}
                      </button>
                      <button
                        type="button"
                        data-testid={`req-service-${e.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-[#ef4444] hover:bg-[#dc2626] py-2 rounded transition-colors"
                      >
                        <Wrench className="w-3.5 h-3.5" /> {p.requestService}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* History tab */}
        {tab === "history" && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {SERVICE_HISTORY.filter(s => s.status === "completed").map((h, i) => (
              <div key={i} data-testid={`history-${i}`} className="p-5 border-b border-slate-100 last:border-b-0 flex flex-wrap items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-green-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-2 items-center mb-1">
                    <span className="font-display text-sm text-[#0891b2]">{h.equipment}</span>
                    <span className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-600 rounded px-2 py-0.5 font-semibold">{h.type}</span>
                  </div>
                  <p className="text-sm text-slate-600">{h.note}</p>
                  <div className="text-xs text-slate-400 mt-1" dir="ltr">{h.date} · {h.tech}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Schedule tab */}
        {tab === "schedule" && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {SERVICE_HISTORY.filter(s => s.status === "scheduled").map((h, i) => (
              <div key={i} data-testid={`schedule-${i}`} className="p-5 border-b border-slate-100 last:border-b-0 flex flex-wrap items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-amber-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm text-[#0891b2] mb-1">{h.equipment}</div>
                  <p className="text-sm text-slate-600">{h.note}</p>
                  <div className="text-xs text-slate-400 mt-1" dir="ltr">{h.date} · {h.tech}</div>
                </div>
                <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-700 rounded px-2 py-0.5 font-semibold">{p.scheduled}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* QR Modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setSelected(null)}
          data-testid="qr-modal"
        >
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 font-mono">{selected.id}</div>
            <h3 className="font-display text-lg text-[#0891b2] mb-4">{lang === "ar" ? selected.name_ar : selected.name_en}</h3>
            <div className="bg-white border-4 border-[#0891b2] p-4 rounded-lg inline-block">
              <img
                alt="QR"
                className="w-40 h-40"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(`https://mariotkitchen.com/asset/${selected.id}`)}`}
              />
            </div>
            <div className="text-xs text-slate-500 mt-4">{p.qrHint}</div>
            <Button type="button" onClick={() => setSelected(null)} className="mt-4 bg-[#0891b2] hover:bg-[#0e7490] text-white w-full">{p.close}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
